import { BarChart3, TrendingUp, AlertTriangle, DollarSign, Users, FileText, Clock, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Spinner } from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { useIncidentStatistics } from '../hooks/useIncidents';
import { DEGREE_LABELS } from '../types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const COLORS = ['#3b82f6', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899'];

const DEGREE_COLORS: Record<string, string> = {
  D0: '#6b7280',
  D1: '#3b82f6',
  D2: '#f59e0b',
  D3: '#ef4444',
  D4: '#dc2626',
  D5: '#991b1b',
};

// Custom Tooltip Component with better contrast
interface TooltipPayload {
  name: string;
  value: number | string;
  color: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
}

const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-3">
        <p className="font-semibold text-gray-900 mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm text-gray-700">
            <span className="font-medium">{entry.name}:</span>{' '}
            <span className="font-bold" style={{ color: entry.color }}>
              {typeof entry.value === 'number' ? entry.value.toLocaleString('pt-BR') : entry.value}
            </span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export function StatisticsPage() {
  const { data: stats, isLoading } = useIncidentStatistics();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-6">
        <p className="text-center text-muted-foreground">Nenhuma estatística disponível</p>
      </div>
    );
  }

  // Preparar dados para gráficos
  const degreeChartData = Object.entries(stats.byDegree || {}).map(([degree, count]) => ({
    name: DEGREE_LABELS[degree as keyof typeof DEGREE_LABELS],
    value: count,
    degree,
  }));

  const monthlyChartData = (stats.monthlyIncidents || []).map(item => ({
    month: format(new Date(item.month + '-01'), 'MMM/yy', { locale: ptBR }),
    incidentes: item.count,
  }));

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Estatísticas de Incidentes</h1>
        <p className="text-muted-foreground">Visão geral e métricas do sistema de gestão de incidentes</p>
      </div>

      {/* SEÇÃO 1: VISÃO GERAL - KPIs Principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-blue-500" />
              Total de Incidentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.totalIncidents}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-red-500" />
              Prejuízo Total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600">
              R$ {stats.totalLoss.toFixed(2)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-green-500" />
              Empresa Absorveu
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              R$ {stats.companyAbsorbed.toFixed(2)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-orange-500" />
              Reincidências
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600">
              {stats.reincidenceRate}%
            </div>
          </CardContent>
        </Card>
      </div>

      {/* SEÇÃO 2: ALERTAS E PENDÊNCIAS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="w-4 h-4 text-orange-500" />
              Incidentes Pendentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.pendingApproval}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-yellow-500" />
              Acordos Pendentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.pendingAgreements}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-500" />
              Assinaturas Pendentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.pendingDocuments}</div>
          </CardContent>
        </Card>
      </div>

      {/* SEÇÃO 3: RANKING DE COLABORADORES E RISCOS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Ranking de Colaboradores */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Ranking de Colaboradores
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left text-sm text-muted-foreground">
                    <th className="pb-2">#</th>
                    <th className="pb-2">Colaborador</th>
                    <th className="pb-2 text-right">Incidentes</th>
                    <th className="pb-2 text-right">Prejuízo</th>
                    <th className="pb-2 text-right">Crédito</th>
                    <th className="pb-2 text-center">Grau</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.employeeRanking?.slice(0, 10).map((employee, index) => (
                    <tr key={employee.employeeId} className="border-b last:border-0">
                      <td className="py-3 text-sm font-medium">{index + 1}</td>
                      <td className="py-3 text-sm">{employee.employeeName}</td>
                      <td className="py-3 text-sm text-right">{employee.totalIncidents}</td>
                      <td className="py-3 text-sm text-right text-red-600">
                        R$ {employee.totalLoss.toFixed(2)}
                      </td>
                      <td className="py-3 text-sm text-right text-green-600">
                        R$ {employee.creditBalance.toFixed(2)}
                      </td>
                      <td className="py-3 text-center">
                        <Badge
                          variant={
                            employee.currentDegree === 'D0' || employee.currentDegree === 'D1'
                              ? 'default'
                              : employee.currentDegree === 'D2'
                              ? 'warning'
                              : 'error'
                          }
                        >
                          {DEGREE_LABELS[employee.currentDegree as keyof typeof DEGREE_LABELS] || employee.currentDegree}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Colaboradores em Risco */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                Colaboradores em Risco
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.employeesAtRisk}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-blue-500" />
                Pendentes Reset
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.pendingResets}</div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* SEÇÃO 4: ANÁLISE FINANCEIRA */}
      <div className="grid grid-cols-1 gap-6">
        {/* Prejuízos Graves */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Prejuízos Graves (&gt; R$1.000)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.severeIncidents?.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                Nenhum prejuízo grave registrado
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left text-sm text-muted-foreground">
                      <th className="pb-2">Colaborador</th>
                      <th className="pb-2">Categoria</th>
                      <th className="pb-2 text-right">Valor</th>
                      <th className="pb-2 text-center">Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.severeIncidents?.slice(0, 10).map((incident) => (
                      <tr key={incident.id} className="border-b last:border-0">
                        <td className="py-3 text-sm font-medium">{incident.employeeName}</td>
                        <td className="py-3 text-sm text-muted-foreground">{incident.categoryName}</td>
                        <td className="py-3 text-sm text-right font-bold text-red-600">
                          R$ {incident.amount.toFixed(2)}
                        </td>
                        <td className="py-3 text-sm text-center text-muted-foreground">
                          {format(new Date(incident.date), 'dd/MM/yyyy')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* SEÇÃO 5: DISTRIBUIÇÃO */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Incidentes por Grau */}
        <Card>
          <CardHeader>
            <CardTitle>Incidentes por Grau</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={degreeChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {degreeChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={DEGREE_COLORS[entry.degree] || COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top 5 Categorias */}
        <Card>
          <CardHeader>
            <CardTitle>Top 5 Categorias</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.topCategories} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="categoryName" type="category" width={120} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="Total" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* SEÇÃO 6: TENDÊNCIAS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Gráfico de Incidentes por Mês */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Incidentes por Mês (Últimos 12 Meses)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line type="monotone" dataKey="incidentes" stroke="#3b82f6" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Categorias com Maior Prejuízo */}
        <Card>
          <CardHeader>
            <CardTitle>Categorias - Maior Prejuízo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.categoriesByLoss?.slice(0, 5).map((category, index) => (
                <div key={category.categoryId} className="flex justify-between items-center p-2 bg-muted rounded">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-muted-foreground">{index + 1}.</span>
                    <span className="text-sm font-medium">{category.categoryName}</span>
                  </div>
                  <span className="text-sm font-bold text-red-600">
                    R$ {category.totalLoss.toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
